import { Box, Stack, Typography, useTheme } from '@mui/material';
import type { ComponentType, ReactNode } from 'react';
import type { SvgIconProps } from '@mui/material';

/**
 * Polished empty-state used by every "this PR isn't built yet" placeholder
 * in PR-A. Dual-purpose: it's also what real list pages show when there
 * are zero rows, so we land it once and reuse.
 */
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
}: {
  icon?: ComponentType<SvgIconProps>;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
}) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        py: 10,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        bgcolor: theme.palette.surface.card,
        border: `1px solid ${theme.palette.border.subtle}`,
        borderRadius: theme.radius.md + 'px',
      }}
    >
      <Stack spacing={2} alignItems="center" maxWidth={460}>
        {Icon && (
          <Box
            sx={{
              width: 48,
              height: 48,
              display: 'grid',
              placeItems: 'center',
              borderRadius: theme.radius.md + 'px',
              bgcolor: theme.palette.brand.lighter,
              color: theme.palette.brand.dark,
            }}
          >
            <Icon sx={{ fontSize: 24 }} />
          </Box>
        )}
        <Typography variant="h3" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        {description && (
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        )}
        {action && <Box sx={{ pt: 1 }}>{action}</Box>}
      </Stack>
    </Box>
  );
}
